import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { fetchCasinoConfigAction } from '@pp/core/src/store/apiActions/fetchCasinoConfigAction';
import { fetchEnvAction } from '@pp/core/src/store/apiActions/fetchEnvAction';
import { fetchPlayerConfigAction } from '@pp/core/src/store/apiActions/fetchPlayerConfigAction';
import { fetchPlayerConfigSyncAction } from '@pp/core/src/store/apiActions/fetchPlayerConfigSyncAction';
import { fetchTableConfigAction } from '@pp/core/src/store/apiActions/fetchTableConfigAction';
import { fetchUserInfoAction } from '@pp/core/src/store/apiActions/fetchUserInfoAction';
import { fetchVersionsAction } from '@pp/core/src/store/apiActions/fetchVersionsAction';
import type { BetType } from '@pp/core/src/store/slices/bets/types';

import { Bets } from '../../../config/bets';

import { CRASH_AUTOPLAY_CONFIG_DEFAULTS } from './constants';
import {
  AutoplayStartOptions,
  AutoplayRoundCountOptions,
  AutoplayBetsSpot,
  AutoplayIsThisRoundOnly,
  AutoCashoutCheck,
  SetAutoPlayBetType,
  AutoPlayModifiedCheck,
  CrashAutoplayLimits,
  Cancelled,
  InfiniteAutoPlay,
  AutoplayRounds,
} from './types';

export interface BetBoards {
  [Bets.ONE]: boolean;
  [Bets.TWO]: boolean;
}
export interface BetInfiniEnabled {
  [Bets.ONE]: boolean | null;
  [Bets.TWO]: boolean | null;
}
export interface BetBoardsRound {
  [Bets.ONE]: number;
  [Bets.TWO]: number;
}
export interface BetBoardsRoundOptions {
  [Bets.ONE]: number[];
  [Bets.TWO]: number[];
}
export interface AutoPlayJurisdiction {
  [Bets.ONE]: number | null;
  [Bets.TWO]: number | null;
}
export interface BetBoardsAutoCashout {
  [Bets.ONE]: { isEnable: boolean; value: string };
  [Bets.TWO]: { isEnable: boolean; value: string };
}
export interface AutoPlayBetType {
  [Bets.ONE]: { betType: BetType | null; isEnable: boolean; value: string };
  [Bets.TWO]: { betType: BetType | null; isEnable: boolean; value: string };
  gameId: string | null;
}
export interface AutoPlayModified {
  [Bets.ONE]: { isBetChanged: boolean | null; isAutoCashoutChanged: boolean | null; isNoChangesToShow: boolean | null };
  [Bets.TWO]: { isBetChanged: boolean | null; isAutoCashoutChanged: boolean | null; isNoChangesToShow: boolean | null };
}

const initialState: {
  isOpen: BetBoards;
  isCancelled: BetBoards;
  isInProgress: BetBoards;
  savedBet: AutoPlayBetType;
  roundsLeft: BetBoardsRound;
  balanceChange: BetBoardsRound; // This value represents balance change for autoplay session. Used for limit checks
  singleWinLimit: AutoPlayJurisdiction;
  balanceIncreaseLimit: AutoPlayJurisdiction;
  balanceDecreaseLimit: AutoPlayJurisdiction;
  isLaunchedMidRound: BetBoards;
  isThisRoundOnly: BetBoards;
  savedAutoCashoutValue: BetBoardsAutoCashout;
  isEnabled: BetInfiniEnabled;
  showModifiedPopUp: AutoPlayModified;
  totalRounds: BetBoardsRound;
  maxRoundsLimit: BetBoardsRound;
  roundsOptions: BetBoardsRoundOptions;
  roundsAddons: BetBoardsRoundOptions;
} = {
  isOpen: { [Bets.ONE]: false, [Bets.TWO]: false },
  isCancelled: { [Bets.ONE]: false, [Bets.TWO]: false },
  isInProgress: { [Bets.ONE]: false, [Bets.TWO]: false },
  savedBet: {
    [Bets.ONE]: {
      betType: {
        betSpots: {
          1: { value: 0 },
        },
      },
      isEnable: false,
      value: '1.01',
    },
    [Bets.TWO]: {
      betType: {
        betSpots: {
          2: { value: 0 },
        },
      },
      isEnable: false,
      value: '1.01',
    },
    gameId: '',
  },
  roundsLeft: { [Bets.ONE]: 0, [Bets.TWO]: 0 },
  balanceChange: { [Bets.ONE]: 0, [Bets.TWO]: 0 },
  singleWinLimit: { [Bets.ONE]: null, [Bets.TWO]: null },
  balanceIncreaseLimit: { [Bets.ONE]: null, [Bets.TWO]: null },
  balanceDecreaseLimit: { [Bets.ONE]: null, [Bets.TWO]: null },
  isLaunchedMidRound: { [Bets.ONE]: false, [Bets.TWO]: false },
  isThisRoundOnly: { [Bets.ONE]: false, [Bets.TWO]: false },
  savedAutoCashoutValue: {
    [Bets.ONE]: { isEnable: false, value: '1.01' },
    [Bets.TWO]: { isEnable: false, value: '1.01' },
  },
  isEnabled: { [Bets.ONE]: null, [Bets.TWO]: null },
  showModifiedPopUp: {
    [Bets.ONE]: { isBetChanged: false, isAutoCashoutChanged: false, isNoChangesToShow: false },
    [Bets.TWO]: { isBetChanged: false, isAutoCashoutChanged: false, isNoChangesToShow: false },
  },
  totalRounds: { [Bets.ONE]: 0, [Bets.TWO]: 0 },
  maxRoundsLimit: { [Bets.ONE]: 0, [Bets.TWO]: 0 },
  roundsOptions: { [Bets.ONE]: [], [Bets.TWO]: [] },
  roundsAddons: { [Bets.ONE]: [], [Bets.TWO]: [] },
};

export const crashAutoplaySlice = createSlice({
  name: 'crashAutoplay',
  initialState,
  reducers: {
    saveBet: (state, action: PayloadAction<SetAutoPlayBetType>) => {
      state.savedBet[action.payload.bestSpot] = action.payload.savedBet;
    },
    start: (state, action: PayloadAction<AutoplayStartOptions>) => {
      state.isInProgress[action.payload.bestSpot] = true;
      state.balanceChange[action.payload.bestSpot] = 0;
      state.isLaunchedMidRound[action.payload.bestSpot] = action?.payload?.midRound || false;
    },
    stop: (state, action: PayloadAction<AutoplayBetsSpot>) => {
      state.isInProgress[action.payload.betsSpot] = false;
      state.balanceChange[action.payload.betsSpot] = 0;
      state.balanceIncreaseLimit[action.payload.betsSpot] = null;
      state.balanceDecreaseLimit[action.payload.betsSpot] = null;
      state.singleWinLimit[action.payload.betsSpot] = null;
      state.roundsLeft[action.payload.betsSpot] = 0;
      state.totalRounds[action.payload.betsSpot] = 0;
    },
    setRounds: (state, action: PayloadAction<AutoplayRounds>) => {
      state.roundsLeft[action.payload.betsSpot] = action.payload.value;
      state.totalRounds[action.payload.betsSpot] = action.payload.value;
    },
    addRounds: (state, action: PayloadAction<AutoplayRounds>) => {
      state.totalRounds[action.payload.betsSpot] += getSafeAddonValue(
        state.totalRounds[action.payload.betsSpot],
        action.payload.value,
        state.maxRoundsLimit[action.payload.betsSpot]
      );
      state.roundsLeft[action.payload.betsSpot] += getSafeAddonValue(
        state.roundsLeft[action.payload.betsSpot],
        action.payload.value,
        state.maxRoundsLimit[action.payload.betsSpot]
      );
    },
    updateRoundCount: (state, action: PayloadAction<AutoplayRoundCountOptions>) => {
      state.roundsLeft[action.payload.bestSpot] = action.payload.count;
    },
    updateBalanceChange: (state, action: PayloadAction<AutoplayRoundCountOptions>) => {
      state.balanceChange[action.payload.bestSpot] += action.payload.count;
    },
    setLimits: (state, action: PayloadAction<CrashAutoplayLimits>) => {
      state.balanceChange[action.payload.bestSpot] = 0;
      state.singleWinLimit[action.payload.bestSpot] = action.payload.singleWinLimit || null;
      state.balanceIncreaseLimit[action.payload.bestSpot] = action.payload.balanceIncreaseLimit || null;
      state.balanceDecreaseLimit[action.payload.bestSpot] = action.payload.balanceDecreaseLimit || null;
    },
    decRoundCount: (state, action: PayloadAction<AutoplayRoundCountOptions>) => {
      state.roundsLeft[action.payload.bestSpot] -= 1;
      state.isLaunchedMidRound[action.payload.bestSpot] = false;
    },
    toggleOpen: (state, action: PayloadAction<Bets>) => {
      const betid: Bets = action.payload == 1 ? 2 : 1;
      state.isOpen[betid] = false;
      state.isOpen[action.payload] = !state.isOpen[action.payload];
    },
    openModal: (state, action: PayloadAction<Bets>) => {
      const betid: Bets = action.payload == 1 ? 2 : 1;
      state.isOpen[betid] = false;
      state.isOpen[action.payload] = true;
    },
    setOpenToClose: (state, action: PayloadAction<Bets>) => {
      state.isOpen[action.payload] = false;
    },
    setIsCancelled: (state, action: PayloadAction<Cancelled>) => {
      state.isCancelled[action.payload.bestSpot] = action.payload.cancelled;
    },
    setIsThisRoundOnly: (state, action: PayloadAction<AutoplayIsThisRoundOnly>) => {
      state.isThisRoundOnly[action.payload.bestSpot] = action.payload.thisRoundonly || false;
    },
    setSavedAutoCashoutValue: (state, action: PayloadAction<AutoCashoutCheck>) => {
      state.savedAutoCashoutValue[action.payload.bestSpot] = action.payload.autoCahout;
    },
    setInfiniteAutoplayEnabled: (state, action: PayloadAction<Bets>) => {
      state.isEnabled[action.payload] = !state.isEnabled[action.payload];
    },
    setInfiniteAutoplayEnabledOnCalncel: (state, action: PayloadAction<InfiniteAutoPlay>) => {
      state.isEnabled[action.payload.bestSpot] = action.payload.cancelled;
    },
    setAutoplayEnabledChanged: (state, action: PayloadAction<AutoPlayModifiedCheck>) => {
      state.showModifiedPopUp[action.payload.bestSpot] = action.payload.autoPlayModified;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTableConfigAction.fulfilled, (state, action) => {
        state.roundsOptions = {
          [Bets.ONE]: action.payload.autoplayConfig?.roundsCount ?? CRASH_AUTOPLAY_CONFIG_DEFAULTS.roundsOptions,
          [Bets.TWO]: action.payload.autoplayConfig?.roundsCount ?? CRASH_AUTOPLAY_CONFIG_DEFAULTS.roundsOptions,
        };

        state.roundsAddons = {
          [Bets.ONE]: action.payload.autoplayConfig?.roundStepper ?? CRASH_AUTOPLAY_CONFIG_DEFAULTS.roundsAddons,
          [Bets.TWO]: action.payload.autoplayConfig?.roundStepper ?? CRASH_AUTOPLAY_CONFIG_DEFAULTS.roundsAddons,
        };
        state.maxRoundsLimit = {
          [Bets.ONE]: action.payload.autoplayConfig?.maxRounds ?? CRASH_AUTOPLAY_CONFIG_DEFAULTS.maxRoundsLimit,
          [Bets.TWO]: action.payload.autoplayConfig?.maxRounds ?? CRASH_AUTOPLAY_CONFIG_DEFAULTS.maxRoundsLimit,
        };
      })
      // Errors in which we should stop autoplay as the game flow is interrupted
      .addCase(fetchCasinoConfigAction.rejected, (state) => {
        state.isInProgress[Bets.ONE] = false;
        state.isInProgress[Bets.TWO] = false;
      })
      .addCase(fetchEnvAction.rejected, (state) => {
        state.isInProgress[Bets.ONE] = false;
        state.isInProgress[Bets.TWO] = false;
      })
      .addCase(fetchPlayerConfigAction.rejected, (state) => {
        state.isInProgress[Bets.ONE] = false;
        state.isInProgress[Bets.TWO] = false;
      })
      .addCase(fetchPlayerConfigSyncAction.rejected, (state) => {
        state.isInProgress[Bets.ONE] = false;
        state.isInProgress[Bets.TWO] = false;
      })
      .addCase(fetchTableConfigAction.rejected, (state) => {
        state.isInProgress[Bets.ONE] = false;
        state.isInProgress[Bets.TWO] = false;
      })
      .addCase(fetchUserInfoAction.rejected, (state) => {
        state.isInProgress[Bets.ONE] = false;
        state.isInProgress[Bets.TWO] = false;
      })
      .addCase(fetchVersionsAction.rejected, (state) => {
        state.isInProgress[Bets.ONE] = false;
        state.isInProgress[Bets.TWO] = false;
      });
  },
});

export const crashAutoplayActions = crashAutoplaySlice.actions;

// Utils
const getSafeAddonValue = (currentValue: number, addOn: number, maxLimit: number): number => {
  const remainingCapacity = maxLimit - currentValue;

  if (remainingCapacity <= 0) {
    return 0;
  }

  return Math.min(remainingCapacity, addOn);
};
